import { humanId } from 'human-id';
import { z } from 'zod';

import { createProposedActionsApi } from '@/shared/api/lotus';

import { defineToolSpec, toToolInputJsonSchema } from '../../types';
import {
  buildApplyModificationsErrorText,
  linkSchema,
  nonEmptyStringSchema,
  proposedActionPayloadSchema,
  toApiPayload,
  type ApiAddModification,
} from './shared';

function generateActionId(): string {
  return humanId({ separator: '-', capitalize: false });
}

const inputSchema = z.object({
  link: linkSchema,
  title: nonEmptyStringSchema,
  justification: nonEmptyStringSchema,
  payload: proposedActionPayloadSchema,
  alternative_payloads: z.array(proposedActionPayloadSchema).optional(),
  depends_on_action_external_ids: z.array(nonEmptyStringSchema).optional(),
});

type CreateProposedActionInput = z.infer<typeof inputSchema>;

const TOOL_NAME = 'create_proposed_action';

export function createCreateProposedActionToolSpec(patientUserId: string) {
  const api = createProposedActionsApi();

  return defineToolSpec({
    name: TOOL_NAME,
    inputSchema,
    getDefinition: () => ({
      name: TOOL_NAME,
      description:
        "Create one new proposed clinical action in the patient's care plan. " +
        'The action ID is generated automatically — do NOT provide one. ' +
        'Provide a typed payload object for exactly one of: draft_prescription, draft_existing_lab, or send_follow_up. ' +
        'Example prescription payload: {"payload_tool_name":"draft_prescription","payload_id":"rx-1","payload_justification":"Start first-line therapy","dispensableDrugId":12345,"directions":"Take 1 tablet by mouth daily","quantity":30,"refills":0,"daysSupply":30}. ' +
        'Example lab payload: {"payload_tool_name":"draft_existing_lab","payload_id":"lab-1","payload_justification":"Baseline monitoring","labTestId":"abc-123","labTestName":"Comprehensive Metabolic Panel","icdCodes":[{"code":"I10","description":"Essential hypertension"}]}. ' +
        'Example follow-up payload: {"payload_tool_name":"send_follow_up","payload_id":"fu-1","payload_justification":"Communicate next steps","title":"Follow-up","content":"Please complete your labs this week."}.',
      input_schema: toToolInputJsonSchema(inputSchema),
    }),
    async handle(input: CreateProposedActionInput) {
      try {
        const externalId = generateActionId();
        const modification: ApiAddModification = {
          type: 'add',
          external_id: externalId,
          link: input.link,
          title: input.title,
          payload: toApiPayload(input.payload),
          justification: input.justification,
        };

        if (input.alternative_payloads !== undefined) {
          modification.alternatives = input.alternative_payloads.map(toApiPayload);
        }
        if (input.depends_on_action_external_ids !== undefined) {
          modification.depends_on_action_external_ids = input.depends_on_action_external_ids;
        }

        const result = await api.applyModifications(patientUserId, [modification]);
        return { output: { ...result, generated_external_id: externalId } };
      } catch (error: unknown) {
        return { errorText: buildApplyModificationsErrorText(error) };
      }
    },
  });
}
